import React, { useEffect, useRef, useState } from "react";
import { Container } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLoader } from "../../context/LoaderContext";
import List from "./List";
import AddEditProfileSuggestionCard from "./AddEditProfileSuggestionCard";
import {
    fetchProfileSuggestionCards,
    createProfileSuggestionCard,
    updateProfileSuggestionCard,
} from "../../redux/slice/profileSuggestionCardSlice";
import { useToast } from "../../context/TostContext";

const ProfileSuggestionCards = () => {
    const dispatch = useDispatch();
    const { setLoading } = useLoader();
    const toast = useToast();
    const [limit, setLimit] = useState(10);

    const { list = [], isLoading, total } = useSelector((state) => state.profileSuggestionCards || {});

    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const isInitialLoadRef = useRef(true);
    const searchTimeout = useRef(null);

    // Modal States
    const [showModal, setShowModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [isView, setIsView] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);

    useEffect(() => {
        if (searchTimeout.current) {
            clearTimeout(searchTimeout.current);
        }

        const fetchData = () => {
            if (isInitialLoadRef.current) {
                setLoading(true);
            }
            dispatch(
                fetchProfileSuggestionCards({
                    page: currentPage,
                    search: searchTerm,
                    limit: limit,
                })
            ).finally(() => {
                if (isInitialLoadRef.current) {
                    setLoading(false);
                    isInitialLoadRef.current = false;
                }
            });
        };

        if (isInitialLoadRef.current) {
            fetchData();
        } else {
            searchTimeout.current = setTimeout(fetchData, 500);
        }

        return () => {
            if (searchTimeout.current) {
                clearTimeout(searchTimeout.current);
            }
        };
    }, [searchTerm, currentPage, limit, dispatch, setLoading]);

    const handleSearchChange = (value) => {
        setSearchTerm(value);
        setCurrentPage(1);
    };

    const handleAddBtn = () => {
        setSelectedItem(null);
        setIsView(false);
        setShowModal(true);
    };

    const handleEditBtn = (item) => {
        setSelectedItem(item);
        setIsView(false);
        setShowModal(true);
    };

    const handleViewBtn = (item) => {
        setSelectedItem(item);
        setIsView(true);
        setShowModal(true);
    };

    const handleLimitChange = (newLimit) => {
        setLimit(newLimit);
        setCurrentPage(1);
    };

    const refreshList = () => {
        dispatch(
            fetchProfileSuggestionCards({
                search: searchTerm,
                page: currentPage,
                limit: limit,
            })
        );
    };

    const handleModalSubmit = async (formData) => {
        setModalLoading(true);
        try {
            let result;
            if (selectedItem) {
                result = await dispatch(
                    updateProfileSuggestionCard({ id: selectedItem.id, formData })
                ).unwrap();
            } else {
                result = await dispatch(createProfileSuggestionCard(formData)).unwrap();
            }

            if (result?.success) {
                toast.success(result.message || "Profile suggestion card saved successfully!");
                setShowModal(false);
                setSelectedItem(null);
                refreshList();
            }
        } catch (error) {
            toast.error(error.message || "Failed to save card");
        } finally {
            setModalLoading(false);
        }
    };

    return (
        <Container fluid className="px-0">
            <div className="bg-white rounded-2 overflow-hidden common-shadow mt-4">
                <List
                    data={list}
                    isLoading={isLoading}
                    isInitialLoad={isInitialLoadRef.current}
                    searchTerm={searchTerm}
                    setSearchTerm={handleSearchChange}
                    setCurrentPage={setCurrentPage}
                    currentPage={currentPage}
                    handleAddBtn={handleAddBtn}
                    handleEditBtn={handleEditBtn}
                    onView={handleViewBtn}
                    refresh={refreshList}
                    limit={limit}
                    setLimit={handleLimitChange}
                    total={total}
                />
            </div>

            <AddEditProfileSuggestionCard
                isOpen={showModal}
                onClose={() => {
                    setShowModal(false);
                    setSelectedItem(null);
                }}
                onSubmit={handleModalSubmit}
                item={selectedItem}
                isLoading={modalLoading}
                isView={isView}
                title={
                    isView
                        ? "View Profile Suggestion"
                        : selectedItem
                        ? "Update Profile Suggestion"
                        : "Add Profile Suggestion"
                }
                size="lg"
            />
        </Container>
    );
};

export default ProfileSuggestionCards;
